// newsPodcast — shared, wall-clock-synced podcast feeds for the flat "TV in a
// window" screens around the world.
//
// A podcast EPISODE carries a `visual_timeline`: an ordered cue sheet
// ({ startTime (s), mediaUrl }) force-aligned to the narration on the web
// /listen page (PodcastHeroStage). Here we render those same slides on world
// screens — SYNCED across every screen and every client by driving the cue index
// off the wall clock modulo the episode duration, the same time-offset trick the
// radio stations use. Nothing is stored, and a visitor arriving late sees what
// everyone else is seeing.
//
// TWO FEEDS, NOT ONE (Khari, 2026-08-04: "the interview podcasts are just
// rotating.. they should be playing episodes"). A screen set to News and a
// screen set to Interview are different televisions showing different shows, so
// each kind loads and syncs independently.
//
// The news-named exports are kept as thin `kind: 'news'` wrappers because
// NewsTv already uses them and its behaviour is unchanged.
import * as THREE from 'three';
import { API_BASE, IS_MOBILE } from '../config';
import { audioEngine } from './audioEngine';
import { proxied } from './assets';

// THE FILMED SHOW, NOT THE PODCAST (Khari, 2026-10-23: "It is playing the news
// podcast, but it should also be playing the video version instead (this goes
// for all panels displaying the Signal & Soul News show)"). When the episode has
// a video (`video_url`), every screen of that kind shows ONE shared <video> —
// one decoder however many screens — on the wall clock, and its own soundtrack
// is the screen's sound (the separate podcast audio and the slides stand down).
// An episode without a film keeps the slides and the audio. Not on a phone: a
// video decoder on top of the city is what kills an iPhone tab (the DJ cameo's
// lesson), so phones keep the slides.
const EPISODE_VIDEO = !IS_MOBILE;

export const KINDS = ['news', 'interview'];

// GCS images are CORS-gated for THREE.TextureLoader; route them through the API
// asset proxy (same approach as Interior.jsx proxiedMedia).
export function proxyMedia(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'storage.googleapis.com') {
      const parts = u.pathname.replace(/^\//, '').split('/');
      const bucket = parts.shift();
      const name = parts.map(decodeURIComponent).join('/');
      return `${API_BASE}/api/assets/file?bucket=${encodeURIComponent(bucket)}&name=${encodeURIComponent(name)}`;
    }
  } catch { /* passthrough */ }
  return url;
}

const _promises = new Map();   // kind → in-flight/resolved load
const _feeds = new Map();      // kind → { cues, duration, audioUrl, title, show }

/**
 * WHICH EPISODE THIS KIND OF SCREEN IS SHOWING.
 *
 * `guest_avatar_id` is the discriminator, and PodcastService.getEpisodes says
 * why it is the only one available per episode: `show_format` lives on the host
 * SET, so a set producing both formats reports the same value for either. A
 * guest means an interview; no guest means banter, which is the news show.
 *
 * The previous version matched /news/i against show_format, show_name and
 * title — and against the real catalogue that matches NOTHING (the news show is
 * called "Avva & Devvin" with format 'banter'), so it always fell through to
 * "newest episode of any kind" and the news TVs could quietly be playing an
 * interview. The name test is kept only as a tiebreak among banter episodes.
 */
function pickEpisode(episodes, kind) {
  if (!Array.isArray(episodes) || !episodes.length) return null;
  const filmed = (list) => (EPISODE_VIDEO ? list.find((e) => e.video_url) : null);
  if (kind === 'interview') { const iv = episodes.filter((e) => e.guest_avatar_id); return filmed(iv) || iv[0] || null; }
  const banter = episodes.filter((e) => !e.guest_avatar_id);
  const isNews = (e) => /news/i.test(e.show_format || '') || /news/i.test(e.show_name || '') || /news/i.test(e.title || '');
  const named = banter.filter(isNews);
  // The newest FILMED one first (the video version is the show); then as before.
  return filmed(named) || filmed(banter) || named[0] || banter[0] || null;
}

async function _load(kind) {
  const listRes = await fetch(`${API_BASE}/api/podcast/episodes?limit=24`, { cache: 'no-store' });
  const list = await listRes.json();
  const pick = pickEpisode(list?.episodes, kind);
  if (!pick) throw new Error(`no ${kind} episodes`);

  const epRes = await fetch(`${API_BASE}/api/podcast/episodes/${pick.episode_id}`, { cache: 'no-store' });
  // The single-episode endpoint wraps the object: { episode: {...} }.
  const ep = (await epRes.json())?.episode || {};
  const rawCues = Array.isArray(ep?.visual_timeline?.cues) ? ep.visual_timeline.cues : [];
  const cues = rawCues
    .filter((c) => c && c.mediaUrl)
    .map((c) => ({ t: Number(c.startTime) || 0, url: proxyMedia(c.mediaUrl) }))
    .sort((a, b) => a.t - b.t);

  // Cover as a lone fallback slide if the episode has no timeline yet. A screen
  // holding one slide is still PLAYING that episode — it just has one picture.
  if (cues.length === 0 && (ep?.cover_image_url || pick.cover_image_url)) {
    cues.push({ t: 0, url: proxyMedia(ep.cover_image_url || pick.cover_image_url) });
  }
  const videoUrl = EPISODE_VIDEO && ep?.video_url ? proxied(ep.video_url) : null;
  if (cues.length === 0 && !videoUrl) throw new Error(`no ${kind} slides`);

  const lastT = cues.length ? cues[cues.length - 1].t : 0;
  const duration = Math.max(Number(ep?.duration_seconds) || 0, lastT + 6);
  // Audio plays through a plain <audio> element — use the RAW signed GCS URL
  // (no proxy): cross-origin playback needs no CORS, and GCS supports range
  // requests so seeking (sync-to-clock) works. (Only the IMAGES need proxying,
  // because THREE.TextureLoader samples pixels and IS CORS-gated.)
  const feedObj = {
    cues, duration,
    audioUrl: ep?.audio_url || null,
    videoUrl,
    title: ep?.title || pick.title || (kind === 'interview' ? 'Interview' : 'The News'),
    show: ep?.show_name || pick.show_name || (kind === 'interview' ? 'Interviews' : 'News'),
  };
  _feeds.set(kind, feedObj);
  console.log(`[podcast] ${kind} feed ready — "${feedObj.show}: ${feedObj.title}"`
    + ` · ${cues.length} slide(s) · ${Math.round(duration)}s · ${feedObj.videoUrl ? 'FILMED (video + its sound)' : `audio ${feedObj.audioUrl ? 'yes' : 'no'}`}`);
  return feedObj;
}

/** Kick off (or reuse) the shared load for a kind. Safe to call from many screens. */
export function loadEpisodeFeed(kind = 'news') {
  const k = KINDS.includes(kind) ? kind : 'news';
  if (!_promises.has(k)) {
    _promises.set(k, _load(k).catch((e) => { _promises.delete(k); throw e; }));
  }
  return _promises.get(k);
}

/**
 * The cue index every screen of this kind should be showing right now, from the
 * shared wall clock (all clients agree). Returns -1 before the feed is ready.
 */
export function currentIndexOf(kind = 'news', now = Date.now()) {
  const f = _feeds.get(kind);
  if (!f) return -1;
  const t = (now / 1000) % f.duration;
  let idx = 0;
  for (let i = 0; i < f.cues.length; i++) {
    if (t >= f.cues[i].t) idx = i; else break;
  }
  return idx;
}

export function feedOf(kind = 'news') { return _feeds.get(kind) || null; }

// ── Listening ────────────────────────────────────────────────────────────────
// A screen becomes audible by its own trigger — walking into range, always-on,
// or hover — and the placement layer turns distance into a LEVEL. One shared
// <audio> per KIND serves every screen showing it (they are the same stream), so
// moving between adjacent screens never cuts the audio.
//
// It plays in the AMBIENT layer: on top of the district station, never instead
// of it. The station used to dip underneath, and that was wrong — walking toward
// a television should not stop the city's music (Khari, 2026-08-04: "let the
// user decide that. dont mute it. there will be times where this kills the
// scene").
//
// MUTE (Khari, 2026-08-04: "it should include some sort of mute button too") is
// global and sticky: a screen you have silenced stays silent when you walk away
// and come back, and it silences every podcast surface at once rather than
// per-screen — there is one narration, so there is one switch.
const _audios = new Map();     // kind → HTMLAudioElement
const _hoverCounts = new Map();  // kind → number
let _muted = false;
const _muteSubs = new Set();

// ── The level, from whichever screen is loudest ──────────────────────────────
//
// Every screen showing a kind plays ONE stream (they are the same episode, wall
// clock synced), so per-screen volume cannot live on per-screen elements. Each
// audible screen publishes a level and the element takes the MAX — so a salon TV
// murmuring at 0.25 stays background until you walk up to a jumbotron at 1.0,
// which then wins. Summing them instead would mean two quiet screens are louder
// than either, which is not how a room works.
const _levels = new Map();     // kind → Map(screenId → 0..1)

export function setScreenLevel(kind, screenId, level) {
  const k = KINDS.includes(kind) ? kind : 'news';
  let m = _levels.get(k);
  if (!m) { m = new Map(); _levels.set(k, m); }
  const v = Math.max(0, Math.min(1, Number(level) || 0));
  if (v <= 0.001) m.delete(screenId); else m.set(screenId, v);
  applyLevel(k);
}

export function clearScreenLevel(kind, screenId) {
  _levels.get(kind)?.delete(screenId);
  applyLevel(kind);
}

function loudest(kind) {
  const m = _levels.get(kind);
  if (!m || !m.size) return 0;
  let best = 0;
  m.forEach((v) => { if (v > best) best = v; });
  return best;
}

// Volume follows the loudest screen; the AMBIENT layer decides whether it is in
// the mix at all.
//
// Ambient, not override (Khari, 2026-08-04: "it stops the background music.. let
// the user decide that. dont mute it. there will be times where this kills the
// scene"). A screen is part of a room, so it plays ON TOP of the station rather
// than replacing it — walking toward a television must not stop the city's
// music. The element's own volume is already the distance-aware level, so
// layering needs nothing else.
function applyLevel(kind) {
  if (_feeds.get(kind)?.videoUrl) { applyVideoLevel(kind); return; }
  const a = _audios.get(kind);
  const want = _muted ? 0 : loudest(kind);
  if (!a) { if (want > 0) startAudio(kind); return; }
  try { a.volume = want; } catch { /* noop */ }
  if (want > 0) {
    audioEngine.addAmbient(a);
    if (a.paused) { syncToClock(kind); a.play?.().catch(() => {}); }
  } else {
    audioEngine.removeAmbient(a);
  }
}

export function isPodcastMuted() { return _muted; }
export function onPodcastMute(fn) { _muteSubs.add(fn); return () => _muteSubs.delete(fn); }

export function setPodcastMuted(muted) {
  _muted = !!muted;
  _videos.forEach((_, kind) => applyVideoLevel(kind));
  if (_muted) {
    // Hand the mix back immediately rather than waiting for a pointer-out —
    // pressing mute has to be audible as silence at once.
    _audios.forEach((a) => { try { audioEngine.removeAmbient(a); a.pause?.(); } catch { /* noop */ } });
  } else {
    // Un-muting resumes whatever is currently audible — a hovered screen, or a
    // proximity/always one you are simply standing near — so the button is a
    // toggle rather than something you press and then have to re-trigger.
    KINDS.forEach((kind) => {
      if (loudest(kind) > 0 || (_hoverCounts.get(kind) || 0) > 0) applyLevel(kind);
    });
  }
  _muteSubs.forEach((fn) => { try { fn(_muted); } catch { /* noop */ } });
  console.log(`[podcast] ${_muted ? 'MUTED' : 'unmuted'}`);
}

function ensureAudio(kind) {
  if (_audios.has(kind)) return _audios.get(kind);
  const f = _feeds.get(kind);
  if (!f?.audioUrl) return null;
  const a = new Audio();
  a.preload = 'auto';
  a.loop = true;
  // NO crossOrigin: the signed GCS URL doesn't return Access-Control-Allow-Origin,
  // so a CORS request would be blocked and the audio would never play (the same
  // gotcha the station audio element documents). Plain playback needs no CORS.
  a.src = f.audioUrl;
  _audios.set(kind, a);
  return a;
}

// Snap playback to the moment the slideshow is showing, so audio + slides agree.
function syncToClock(kind) {
  const a = _audios.get(kind);
  const f = _feeds.get(kind);
  if (!a || !f) return;
  const t = (Date.now() / 1000) % f.duration;
  try { if (Math.abs((a.currentTime || 0) - t) > 0.5) a.currentTime = t; } catch { /* not seekable yet */ }
}

function startAudio(kind) {
  if (_feeds.get(kind)?.videoUrl) { applyVideoLevel(kind, (_hoverCounts.get(kind) || 0) > 0 ? 1 : 0); return; }
  if (_muted) return;
  const a = ensureAudio(kind);
  if (!a) return;
  syncToClock(kind);
  // Start at the level the screens are asking for rather than at full and then
  // correcting — a background TV must not bark before it settles.
  try { a.volume = loudest(kind) || 1; } catch { /* noop */ }
  // Into the AMBIENT layer, so the station keeps playing underneath. Re-sync
  // once it's rolling.
  audioEngine.addAmbient(a);
  a.play?.().then(() => syncToClock(kind)).catch(() => {});
}

export function podcastHoverEnter(kind = 'news') {
  _hoverCounts.set(kind, (_hoverCounts.get(kind) || 0) + 1);
  startAudio(kind);
}

export function podcastHoverLeave(kind = 'news') {
  const next = Math.max(0, (_hoverCounts.get(kind) || 0) - 1);
  _hoverCounts.set(kind, next);
  if (next === 0 && _feeds.get(kind)?.videoUrl) { applyVideoLevel(kind); return; }
  if (next === 0) {
    const a = _audios.get(kind);
    if (a) audioEngine.removeAmbient(a);   // stops the screen; the bed was never touched
  }
}

// ── The filmed episode: one shared video per kind ────────────────────────────
const _videos = new Map();      // kind → { el, tex, users, sync }
if (import.meta.env.DEV && typeof window !== 'undefined') window.__episodeVideos = _videos;   // probes only

/** The shared video's texture for a screen showing this kind (or null: slides). Pair with releaseEpisodeVideo. */
export function acquireEpisodeVideo(kind) {
  const f = _feeds.get(kind);
  if (!f?.videoUrl) return null;
  let v = _videos.get(kind);
  if (!v) {
    const el = document.createElement('video');
    el.crossOrigin = 'anonymous';   // the API proxy carries CORS: a texture needs it
    el.loop = true; el.muted = true; el.playsInline = true; el.preload = 'auto';
    el.src = f.videoUrl;
    const tex = new THREE.VideoTexture(el);
    tex.colorSpace = THREE.SRGBColorSpace;
    v = { el, tex, users: 0, sync: null };
    _videos.set(kind, v);
    el.addEventListener('loadedmetadata', () => syncVideo(kind), { once: true });
  }
  v.users += 1;
  if (v.users === 1) {
    syncVideo(kind);
    v.el.play().catch(() => {});
    v.sync = setInterval(() => syncVideo(kind), 3000);
    applyVideoLevel(kind);
  }
  return v.tex;
}

export function releaseEpisodeVideo(kind) {
  const v = _videos.get(kind);
  if (!v) return;
  v.users = Math.max(0, v.users - 1);
  if (v.users === 0) { clearInterval(v.sync); v.sync = null; try { v.el.pause(); v.el.muted = true; } catch { /* gone */ } }
}

// Every screen, every visitor: the same moment of the film (wall clock modulo its length).
function syncVideo(kind) {
  const v = _videos.get(kind);
  const f = _feeds.get(kind);
  if (!v || !f) return;
  const len = Number.isFinite(v.el.duration) && v.el.duration > 1 ? v.el.duration : f.duration;
  const t = (Date.now() / 1000) % len;
  try { if (Math.abs((v.el.currentTime || 0) - t) > 1) v.el.currentTime = t; } catch { /* not seekable yet */ }
}

// The film's own sound, at the loudest screen's level (muted when nobody should hear it).
// iOS ignores `volume` — but phones show the slides anyway (EPISODE_VIDEO).
function applyVideoLevel(kind, floor = 0) {
  const v = _videos.get(kind);
  if (!v) return;
  const want = _muted ? 0 : Math.max(loudest(kind), floor);
  try {
    v.el.volume = Math.max(0, Math.min(1, want));
    v.el.muted = !(want > 0.01) || v.users === 0;
  } catch { /* noop */ }
}

// ── News-named wrappers (NewsTv uses these; behaviour unchanged) ─────────────
export function loadNews() { return loadEpisodeFeed('news'); }
export function currentIndex(now = Date.now()) { return currentIndexOf('news', now); }
export function feed() { return feedOf('news'); }
export function newsHoverEnter() { return podcastHoverEnter('news'); }
export function newsHoverLeave() { return podcastHoverLeave('news'); }
